"use client";

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const AcceptableUsePolicy = () => {
  const { t } = useTranslation();
  const lastUpdatedDate = "2026-02-26";

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true });
    if (!Array.isArray(items)) return null;
    return (
      <ul className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  };

  const Section = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <section className="mb-8">
      <h2 className="font-display text-lg font-semibold text-foreground mb-3">
        {String(t(titleKey))}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );

  const SubSection = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-4 ml-0">
      <h3 className="font-display text-base font-semibold text-foreground mb-2">
        {String(t(titleKey))}
      </h3>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );

  const P = ({ keyName }: { keyName: string }) => <p>{String(t(keyName))}</p>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {String(t("sc.backHome"))}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
              {String(t("aup.title"))}
            </h1>
            <p className="text-sm text-muted-foreground mb-10">
              {String(t("aup.lastUpdated"))}: {lastUpdatedDate}
            </p>

            <p className="text-muted-foreground leading-relaxed mb-2">
              {String(t("aup.intro"))}
            </p>
            <p className="text-muted-foreground leading-relaxed mb-10">
              {String(t("aup.intro2"))}
            </p>

            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none text-foreground">
              <Section titleKey="aup.section1Title">
                <P keyName="aup.section1P1" />
                {renderList("aup.section1List")}
              </Section>

              <Section titleKey="aup.section2Title">
                <P keyName="aup.section2P1" />
                <SubSection titleKey="aup.section2_1Title">
                  {renderList("aup.section2_1List")}
                </SubSection>
                <SubSection titleKey="aup.section2_2Title">
                  {renderList("aup.section2_2List")}
                </SubSection>
                <SubSection titleKey="aup.section2_3Title">
                  {renderList("aup.section2_3List")}
                </SubSection>
                <SubSection titleKey="aup.section2_4Title">
                  {renderList("aup.section2_4List")}
                </SubSection>
                <SubSection titleKey="aup.section2_5Title">
                  {renderList("aup.section2_5List")}
                </SubSection>
              </Section>

              <Section titleKey="aup.section3Title">
                <P keyName="aup.section3P1" />
                <P keyName="aup.section3P2" />
                {renderList("aup.section3List")}
                <P keyName="aup.section3P3" />
              </Section>

              <Section titleKey="aup.section4Title">
                {renderList("aup.section4List")}
              </Section>

              <Section titleKey="aup.section5Title">
                <P keyName="aup.section5P1" />
                {renderList("aup.section5List")}
                <P keyName="aup.section5P2" />
                <P keyName="aup.section5P3" />
              </Section>

              <Section titleKey="aup.section6Title">
                <P keyName="aup.section6P1" />
                <p className="mt-2">{String(t("aup.section6Email"))}</p>
                <p>{String(t("aup.section6Address"))}</p>
              </Section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AcceptableUsePolicy;
